// ============================================================================
// FSA multi-column — Reset rounding (PPE, Intangibles, ageing, Owners' Capital)
// ============================================================================
// Reset rounding on a multi-column note must reset the grid's own rounded
// cells: the backend's save copies column_values[<total>__rounded] onto the
// legacy amount_rounded (SyncMultiColumnPair — column_values wins), so a
// reset of only the legacy fields was silently reverted on save, and the
// movement columns (additions, depreciation, ageing buckets) were never
// re-rounded at all (2026-10-11).
//
// Rule — same as the rest of Reset rounding:
//   • input amount columns: rounded = actual under the CURRENT factor;
//   • formula / drift_formula columns: re-evaluated from those rounded cells
//     (recomputeFormulaColumns — the save-time normaliser);
//   • a class with line items rolls up Σ line items (actual and rounded);
//   • prior_column_values (separate PY ageing grid) and the note-level
//     prior_year_totals entry row reset the same way.

import { autoRound } from '../../shared/rounding';
import { ROUNDED_SUFFIX } from './noteClassDataResolver';
import { recomputeFormulaColumns } from './recomputeFormulaColumns';
import type { FormatColumnSpec } from 'api/types/fsa/template.types';

type Cv = Record<string, number>;

const isFormulaColumn = (c: FormatColumnSpec): boolean =>
  !!((c.is_computed && c.formula) || c.drift_formula);

/** One row's column_values with every amount cell re-rounded under `factor`. */
export function resetColumnValuesRounding(
  cv: Cv | null | undefined,
  columns: FormatColumnSpec[],
  factor: string | null | undefined,
): Cv | undefined {
  if (!cv) return cv ?? undefined;
  const next: Cv = { ...cv };
  for (const c of columns) {
    if (c.data_type !== 'amount' || isFormulaColumn(c)) continue;
    if (typeof next[c.code] === 'number') next[c.code + ROUNDED_SUFFIX] = autoRound(next[c.code], factor);
  }
  return recomputeFormulaColumns(next, columns, factor);
}

/** Σ line items' column_values (actual + rounded) per amount column — the class rollup. */
export function rollupColumnValues(lineItemCvs: Array<Cv | null | undefined>, columns: FormatColumnSpec[]): Cv {
  const out: Cv = {};
  for (const c of columns) {
    if (c.data_type !== 'amount') continue;
    let a = 0;
    let r = 0;
    for (const cv of lineItemCvs) {
      if (typeof cv?.[c.code] === 'number') a += cv[c.code];
      if (typeof cv?.[c.code + ROUNDED_SUFFIX] === 'number') r += cv[c.code + ROUNDED_SUFFIX];
    }
    out[c.code] = a;
    out[c.code + ROUNDED_SUFFIX] = Math.round(r * 100) / 100;
  }
  return out;
}

interface MultiColClass {
  column_values?: Cv | null;
  prior_column_values?: Cv | null;
  line_items?: Array<{ column_values?: Cv | null }>;
}

/**
 * Reset every class (and line item) of a multi-column note. Non-amount
 * keys on a class with line items (e.g. share_pct) are kept as they were.
 */
export function resetMultiColumnClassData<C extends MultiColClass>(
  classData: Record<string, C>,
  columns: FormatColumnSpec[],
  factor: string | null | undefined,
): Record<string, C> {
  const out: Record<string, C> = {};
  for (const [code, cd] of Object.entries(classData)) {
    const lineItems = (cd.line_items ?? []).map(li => (li.column_values
      ? { ...li, column_values: resetColumnValuesRounding(li.column_values, columns, factor) }
      : li));
    const withLines = lineItems.filter(li => li.column_values);
    const classCv = withLines.length > 0
      ? { ...(cd.column_values ?? {}), ...rollupColumnValues(withLines.map(li => li.column_values), columns) }
      : resetColumnValuesRounding(cd.column_values, columns, factor);
    out[code] = {
      ...cd,
      ...(cd.line_items ? { line_items: lineItems } : {}),
      ...(cd.column_values || classCv ? { column_values: classCv } : {}),
      ...(cd.prior_column_values
        ? { prior_column_values: resetColumnValuesRounding(cd.prior_column_values, columns, factor) }
        : {}),
    };
  }
  return out;
}

/** The manual multi-column "Previous Year Total" row: rounded = actual under the current factor. */
export function resetPriorYearTotalsRounding(
  totals: Cv | null | undefined,
  factor: string | null | undefined,
): Cv | undefined {
  if (!totals) return totals ?? undefined;
  const next: Cv = { ...totals };
  for (const [k, v] of Object.entries(totals)) {
    if (k.endsWith(ROUNDED_SUFFIX) || typeof v !== 'number') continue;
    next[k + ROUNDED_SUFFIX] = autoRound(v, factor);
  }
  return next;
}
